import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {TextComponent} [cartDiscountTotal] - Discount line total (only when savings row is shown).
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {Promise<void>} */
  #cartUpdateQueue = Promise.resolve();

  /** @type {number} */
  #cartUpdateGeneration = 0;

  /** Line item keys removed optimistically while requests are still in flight. */
  #pendingRemovalKeys = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      const lineItemKey = this.refs.cartItemRows[line - 1]?.dataset.key;
      if (!lineItemKey) return;
      return this.onLineItemRemove(lineItemKey);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Resolves a cart line item key from declarative click data or the remove button.
   * @param {string | number | { key?: string } | Event} [dataOrEvent]
   * @param {Event} [event]
   * @returns {string | undefined}
   */
  #resolveLineItemKey(dataOrEvent, event) {
    if (dataOrEvent instanceof Event) {
      event = dataOrEvent;
    } else if (typeof dataOrEvent === 'string' && dataOrEvent) {
      return dataOrEvent;
    } else if (typeof dataOrEvent === 'number') {
      return this.refs.cartItemRows[dataOrEvent - 1]?.dataset.key;
    } else if (dataOrEvent && typeof dataOrEvent === 'object' && dataOrEvent.key) {
      return String(dataOrEvent.key);
    }

    const target = event?.target;
    if (!(target instanceof Element)) return;

    const key = target.closest('[data-line-item-key]')?.getAttribute('data-line-item-key');
    return key || undefined;
  }

  /**
   * Handles the line item removal.
   * @param {string | number | { key?: string } | Event} [dataOrEvent]
   * @param {Event} [event]
   */
  onLineItemRemove(dataOrEvent, event) {
    const lineItemKey = this.#resolveLineItemKey(dataOrEvent, event);
    if (!lineItemKey) return;

    const cartItemRowToRemove = this.refs.cartItemRows.find((row) => row.dataset.key === lineItemKey);

    if (!cartItemRowToRemove || cartItemRowToRemove.hasAttribute('data-removing')) return;

    const parentLineItemKey = cartItemRowToRemove.dataset.key;
    if (!parentLineItemKey) return;

    cartItemRowToRemove.setAttribute('data-removing', '');

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === parentLineItemKey),
    ];

    for (const row of rowsToRemove) {
      if (row.dataset.key) {
        this.#pendingRemovalKeys.add(row.dataset.key);
      }
    }

    this.updateQuantity({
      id: lineItemKey,
      quantity: 0,
      action: 'clear',
    });

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.setAttribute('data-removing', '');
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The 1-based line index.
   * @param {string} [config.id] - The line item key (preferred for removals).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const generation = ++this.#cartUpdateGeneration;

    this.#cartUpdateQueue = this.#cartUpdateQueue
      .then(() => this.#performUpdateQuantity(config, generation))
      .catch((error) => {
        console.error(error);
      });

    return this.#cartUpdateQueue;
  }

  /**
   * @param {Object} config
   * @param {number} [config.line]
   * @param {string} [config.id]
   * @param {number} config.quantity
   * @param {string} config.action
   * @param {number} generation
   */
  #performUpdateQuantity(config, generation) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, id, quantity } = config;
    const lineOrId = id ?? line;
    const { cartTotal, cartDiscountTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(id != null ? { id } : { line }),
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    cartDiscountTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors && lineOrId != null) {
          if (id) {
            this.#pendingRemovalKeys.delete(id);
          }
          this.#handleCartError(lineOrId, parsedResponseText);
          return;
        }

        const isLatestResponse = generation === this.#cartUpdateGeneration;
        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        const newSectionHTML = sectionHtml
          ? new DOMParser().parseFromString(sectionHtml, 'text/html')
          : null;

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: isLatestResponse ? parsedResponseText.sections : undefined,
          })
        );

        if (isLatestResponse && sectionHtml) {
          morphSection(this.sectionId, sectionHtml, this.isDrawer ? 'hydration' : 'full');
          this.#syncPendingRemovalsFromCart(parsedResponseText.items);
          this.#prunePendingRemovalRows();
          this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * @param {number} line - 1-based cart line index.
   * @param {Event} event - Change event from the subscription select.
   */
  onSellingPlanChange(line, event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    this.updateSellingPlan(line, event.target);
  }

  /**
   * Updates the selling plan for a line item via the cart change endpoint.
   * @param {number} line - 1-based cart line index.
   * @param {HTMLSelectElement} select - The subscription frequency select.
   */
  updateSellingPlan(line, select) {
    const revertTo =
      select.dataset.lastConfirmed !== undefined ? String(select.dataset.lastConfirmed) : select.value;

    const quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);

    if (!(quantityInput instanceof HTMLInputElement)) {
      console.error('Cart quantity input not found for selling plan update');
      return;
    }

    const quantity = parseInt(quantityInput.value, 10);
    if (Number.isNaN(quantity)) {
      console.error('Invalid quantity for selling plan update');
      return;
    }

    const sellingPlanValue = select.value === '' ? null : Number(select.value);

    const generation = ++this.#cartUpdateGeneration;

    this.#cartUpdateQueue = this.#cartUpdateQueue
      .then(() => this.#performSellingPlanUpdate(line, select, quantity, sellingPlanValue, revertTo, generation))
      .catch((error) => {
        console.error(error);
      });

    return this.#cartUpdateQueue;
  }

  /**
   * @param {number} line
   * @param {HTMLSelectElement} select
   * @param {number} quantity
   * @param {number | null} sellingPlanValue
   * @param {string} revertTo
   * @param {number} generation
   */
  #performSellingPlanUpdate(line, select, quantity, sellingPlanValue, revertTo, generation) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('selling-plan-change:user-action');

    const { cartTotal, cartDiscountTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      selling_plan: sellingPlanValue,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    cartDiscountTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          select.value = revertTo;
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const isLatestResponse = generation === this.#cartUpdateGeneration;
        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        const newSectionHTML = sectionHtml
          ? new DOMParser().parseFromString(sectionHtml, 'text/html')
          : null;

        const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: isLatestResponse ? parsedResponseText.sections : undefined,
          })
        );

        if (isLatestResponse && sectionHtml) {
          morphSection(this.sectionId, sectionHtml, this.isDrawer ? 'hydration' : 'full');
          this.#syncPendingRemovalsFromCart(parsedResponseText.items);
          this.#prunePendingRemovalRows();
          this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch((error) => {
        console.error(error);
        select.value = revertTo;
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Drops keys from the pending-removal set once the server cart no longer contains them.
   * @param {Array<{ key?: string }>} [items]
   */
  #syncPendingRemovalsFromCart(items) {
    if (!items || this.#pendingRemovalKeys.size === 0) return;

    const keysInCart = new Set(items.map((item) => item.key).filter(Boolean));

    for (const key of this.#pendingRemovalKeys) {
      if (!keysInCart.has(key)) {
        this.#pendingRemovalKeys.delete(key);
      }
    }
  }

  /**
   * Removes rows that were optimistically deleted but reappeared from a stale section morph.
   */
  #prunePendingRemovalRows() {
    if (this.#pendingRemovalKeys.size === 0) return;

    for (const key of this.#pendingRemovalKeys) {
      for (const row of this.querySelectorAll(`tr[data-key="${CSS.escape(key)}"]`)) {
        row.remove();
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number | string} lineOrId - The 1-based line index or line item key.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (lineOrId, parsedResponseText) => {
    const quantityInputForLine =
      typeof lineOrId === 'string'
        ? this.querySelector(`tr[data-key="${CSS.escape(lineOrId)}"] input[data-cart-line]`)
        : null;

    const line =
      typeof lineOrId === 'number'
        ? lineOrId
        : Number(
            quantityInputForLine instanceof HTMLInputElement ? quantityInputForLine.dataset.cartLine : NaN
          );

    if (!line) return;

    const quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#prunePendingRemovalRows();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
